/** @file Key channel table: timeline group and property, target kind, value schema, curve rule. */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import type { Curve } from '@pose2d/format/types';

/** A key of any channel as commands handle it (discrete channels never carry `curve`). */
export type AnyKey = { f: number; v: unknown; curve?: Curve | undefined };
/** One target's timelines: timeline property → sorted keys. */
export type KeyTrack = Record<string, AnyKey[] | undefined>;
/** One channel: where its keys live, what `target` names, how values validate, and whether curves are allowed. */
export type ChannelSpec = { group: 'bones' | 'slots' | 'ik'; kind: 'bone' | 'slot' | 'ik'; prop: string; value: z.ZodType; numeric: boolean };

const bone = (prop: string, value: z.ZodType, numeric = true): ChannelSpec => ({ group: 'bones', kind: 'bone', prop, value, numeric });
const slot = (prop: string, value: z.ZodType, numeric = true): ChannelSpec => ({ group: 'slots', kind: 'slot', prop, value, numeric });
const ik = (prop: string, value: z.ZodType, numeric = true): ChannelSpec => ({ group: 'ik', kind: 'ik', prop, value, numeric });

/** Channel name → table row; bone transform values are absolute here and stored setup-relative by setKey. */
export const KEY_CHANNELS = {
  rotation: bone('rotation', prim.finite),
  x: bone('x', prim.finite),
  y: bone('y', prim.finite),
  scaleX: bone('scaleX', prim.finite),
  scaleY: bone('scaleY', prim.finite),
  shearX: bone('shearX', prim.finite),
  shearY: bone('shearY', prim.finite),
  inherit: bone('inherit', prim.inherit, false),
  attachment: slot('attachment', prim.name.nullable(), false),
  rgb: slot('rgb', prim.hex6),
  alpha: slot('alpha', prim.unit),
  dark: slot('dark', prim.hex6),
  ikMix: ik('mix', prim.unit),
  ikSoftness: ik('softness', prim.finite.nonnegative()),
  ikBend: ik('bendPositive', z.boolean(), false),
  ikStretch: ik('stretch', z.boolean(), false),
  ikCompress: ik('compress', z.boolean(), false),
} as const;
